import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileText, Download, Loader2, Search, AlertTriangle,
  CheckCircle, XCircle, Plus, Filter, TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInvoicesPage = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [pending, setPending] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(false);
  const [tab, setTab] = useState('emitted'); // emitted | pending | summary
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [downloading, setDownloading] = useState(null);
  const [creating, setCreating] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchInvoices = async (p = 1, q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 30 });
      if (q) params.append('search', q);
      const res = await axios.get(`${API}/admin/invoices?${params}`, { headers });
      setInvoices(res.data.invoices || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
      setMonthly(res.data.monthly_summary || []);
    } catch { toast.error('Error al cargar facturas'); }
    setLoading(false);
  };

  const fetchPending = async () => {
    setLoadingPending(true);
    try {
      const res = await axios.get(`${API}/admin/invoices/pending`, { headers });
      setPending(res.data.pending || []);
    } catch { toast.error('Error al cargar pendientes'); }
    setLoadingPending(false);
  };

  useEffect(() => { fetchInvoices(); }, []);

  useEffect(() => {
    if (tab === 'pending' && pending.length === 0) fetchPending();
  }, [tab]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchInvoices(1, search);
  };

  const downloadPdf = async (invoiceId) => {
    setDownloading(invoiceId);
    try {
      const res = await axios.get(`${API}/admin/invoices/${invoiceId}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `factura_${invoiceId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar PDF'); }
    setDownloading(null);
  };

  const createManualInvoice = async (item) => {
    setCreating(item.ref_id);
    try {
      await axios.post(`${API}/admin/invoices/create-manual`, {
        user_id: item.user_id,
        description: 'Créditos portal online',
        amount: item.amount,
        ref_id: item.ref_id,
      }, { headers });
      toast.success('Factura creada correctamente');
      fetchPending();
      fetchInvoices(page, search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear factura');
    }
    setCreating(null);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const typeLabel = (t) => {
    const map = { credits: 'Créditos', quick_buy: 'Compra rápida', ticket: 'Ticket', ori_restore: 'ORI Restore', mms_pack: 'Pack MMS', manual: 'Manual' };
    return map[t] || t;
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-invoices-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">FACTURACIÓN</h1>
            <p className="text-zinc-400 mt-1">Gestión completa de facturas del portal</p>
          </div>
          <div className="text-right">
            <p className="text-zinc-500 text-sm">Total facturas</p>
            <p className="text-white font-mono text-2xl font-bold">{total}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="invoice-tabs">
          {[
            { id: 'emitted', label: 'Facturas Emitidas', icon: FileText },
            { id: 'pending', label: 'Pendientes', icon: AlertTriangle },
            { id: 'summary', label: 'Resumen Mensual', icon: TrendingUp },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.id ? 'bg-primary text-white' : 'bg-surface border border-white/10 text-zinc-400 hover:text-white'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" />{t.label}
              {t.id === 'pending' && pending.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pending.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Emitted Invoices Tab */}
        {tab === 'emitted' && (
          <div className="space-y-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por empresa, email, CIF, nº factura..."
                  className="w-full bg-surface border border-white/10 rounded-md pl-10 pr-4 py-2 text-white text-sm" data-testid="invoice-search" />
              </div>
              <Button type="submit" className="bg-primary"><Search className="w-4 h-4" /></Button>
            </form>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : invoices.length === 0 ? (
              <div className="bg-surface border border-white/10 rounded-md p-12 text-center">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No hay facturas emitidas</p>
              </div>
            ) : (
              <div className="bg-surface border border-white/10 rounded-md overflow-x-auto">
                <table className="w-full" data-testid="invoices-table">
                  <thead>
                    <tr className="border-b border-white/10 text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-3">Nº Factura</th>
                      <th className="text-left px-4 py-3">Fecha</th>
                      <th className="text-left px-4 py-3">Cliente</th>
                      <th className="text-left px-4 py-3">CIF</th>
                      <th className="text-left px-4 py-3">Concepto</th>
                      <th className="text-right px-4 py-3">Base</th>
                      <th className="text-right px-4 py-3">IVA</th>
                      <th className="text-right px-4 py-3">Total</th>
                      <th className="text-center px-4 py-3">Holded</th>
                      <th className="text-right px-4 py-3">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white font-mono text-sm font-bold">{inv.invoice_number || '-'}</td>
                        <td className="px-4 py-3 text-zinc-400 text-sm">{formatDate(inv.created_at)}</td>
                        <td className="px-4 py-3 text-white text-sm">{inv.user_company || inv.user_email}</td>
                        <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{inv.fiscal_cif || '-'}</td>
                        <td className="px-4 py-3 text-zinc-300 text-xs max-w-[200px] truncate">{inv.description}</td>
                        <td className="px-4 py-3 text-right text-white text-sm font-mono">{(inv.base_amount || 0).toFixed(2)}€</td>
                        <td className="px-4 py-3 text-right text-zinc-400 text-sm font-mono">{(inv.tax_amount || 0).toFixed(2)}€</td>
                        <td className="px-4 py-3 text-right text-primary text-sm font-mono font-bold">{(inv.total_amount || 0).toFixed(2)}€</td>
                        <td className="px-4 py-3 text-center">
                          {inv.holded_synced ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {inv.holded_invoice_id && (
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv.holded_invoice_id)}
                              disabled={downloading === inv.holded_invoice_id} className="text-primary">
                              {downloading === inv.holded_invoice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { setPage(page - 1); fetchInvoices(page - 1, search); }}>Anterior</Button>
                <span className="text-zinc-400 text-sm py-2">Pág {page}/{totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => { setPage(page + 1); fetchInvoices(page + 1, search); }}>Siguiente</Button>
              </div>
            )}
          </div>
        )}

        {/* Pending Tab */}
        {tab === 'pending' && (
          <div className="space-y-4">
            {loadingPending ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : pending.length === 0 ? (
              <div className="bg-green-500/10 border border-green-500/20 rounded-md p-8 text-center">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
                <p className="text-green-400 font-heading font-bold">TODO AL DÍA</p>
                <p className="text-zinc-400 text-sm mt-1">No hay facturas pendientes de crear</p>
              </div>
            ) : (
              <>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-4">
                  <p className="text-amber-400 text-sm font-medium">{pending.length} compra(s) sin factura</p>
                  <p className="text-zinc-400 text-xs mt-1">Estas compras no tienen factura asociada. Puedes crearlas manualmente si el cliente tiene datos fiscales.</p>
                </div>
                <div className="space-y-2">
                  {pending.map((item, i) => (
                    <div key={i} className="bg-surface border border-white/10 rounded-md p-4 flex items-center gap-4" data-testid={`pending-${i}`}>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{item.user_company || item.user_email}</p>
                        <p className="text-zinc-500 text-xs">{typeLabel(item.type)}{item.ticket_number ? ` ${item.ticket_number}` : ''} — {item.amount}€ (base) — {formatDate(item.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.has_fiscal ? (
                          <span className="text-green-400 text-xs">Datos fiscales OK</span>
                        ) : (
                          <span className="text-red-400 text-xs">Sin datos fiscales</span>
                        )}
                        <Button size="sm" disabled={!item.has_fiscal || creating === item.ref_id}
                          onClick={() => createManualInvoice(item)}
                          className="bg-primary text-white text-xs">
                          {creating === item.ref_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" />Crear Factura</>}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Monthly Summary Tab */}
        {tab === 'summary' && (
          <div className="space-y-4">
            {monthly.length === 0 ? (
              <div className="bg-surface border border-white/10 rounded-md p-12 text-center">
                <TrendingUp className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No hay datos de facturación</p>
              </div>
            ) : (
              <div className="bg-surface border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-3">Mes</th>
                      <th className="text-right px-4 py-3">Facturas</th>
                      <th className="text-right px-4 py-3">Base Imponible</th>
                      <th className="text-right px-4 py-3">IVA</th>
                      <th className="text-right px-4 py-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthly.map(m => (
                      <tr key={m._id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white text-sm font-medium">{m._id}</td>
                        <td className="px-4 py-3 text-right text-zinc-300 text-sm">{m.count}</td>
                        <td className="px-4 py-3 text-right text-white text-sm font-mono">{(m.total_base || 0).toFixed(2)}€</td>
                        <td className="px-4 py-3 text-right text-zinc-400 text-sm font-mono">{(m.total_tax || 0).toFixed(2)}€</td>
                        <td className="px-4 py-3 text-right text-primary text-sm font-mono font-bold">{(m.total || 0).toFixed(2)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminInvoicesPage;
